import { useCallback, useMemo } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'

import { CompanyFragment } from '../../../types/graphql'
import { Select, VStack } from '../../govwell-ui'
import { SelectOption } from '../../govwell-ui/components/Select/types'
import Text from '../Typography/Text'

const getOptionFromCompany = (company: CompanyFragment): SelectOption<CompanyFragment> => ({
  customDisplay: (c) => (
    <VStack>
      <Text>
        {c.name} | {c.emailAddress}
      </Text>
    </VStack>
  ),
  label: company.name,
  value: company,
})

type Props = {
  caption?: string
  companies: CompanyFragment[]
  errorMessage?: string
  getIsOptionDisabled?: (option: SelectOption<CompanyFragment>) => boolean
  isInvalid?: boolean
  isRequired?: boolean
  label?: string
  value: CompanyFragment | undefined
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onValueChange: (value: CompanyFragment | undefined) => void
}
const CompanySelect = ({
  caption,
  companies,
  errorMessage,
  getIsOptionDisabled,
  isInvalid,
  isRequired,
  label,
  value,
  onBlur,
  onValueChange,
}: Props) => {
  const options: SelectOption<CompanyFragment>[] = useMemo(() => {
    return companies.map(getOptionFromCompany)
  }, [companies])

  const selectedOption = useMemo(
    () => options.find((o) => o.value?.id === value?.id),
    [options, value]
  )

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<CompanyFragment> | undefined) => {
      onValueChange(option?.value)
    },
    [onValueChange]
  )

  const getOptionKey = useCallback((c: SelectOption<CompanyFragment>) => `${c.value.id}`, [])

  return (
    <Select
      {...(label ? { label } : { ariaLabel: 'Select company' })}
      caption={caption}
      errorMessage={errorMessage}
      getIsOptionDisabled={getIsOptionDisabled}
      getOptionKey={getOptionKey}
      isInvalid={isInvalid}
      isRequired={isRequired}
      onBlur={onBlur}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      prefixIcon={faSearch}
      selectedOption={selectedOption}
    />
  )
}

export default React.memo(CompanySelect)
